/**
 * Inline artefact references in an answer (H2-206).
 *
 * The agent embeds a chart it produced this turn as `![alt](artifact:<artifactId>)` and links a
 * data file as `[label](artifact:<artifactId>)` — the contract the python_executor tool
 * description publishes to the model, and which the answer harness enforces before delivery.
 * Neither the markdown nor the id is a URL anything can fetch: the artefact lives in a private
 * bucket reachable only through a short-lived signed URL, so these components resolve the id at
 * render time via `useArtifactSignedUrl` and hand the result to the turn's shared image viewer
 * (ADR-22).
 *
 * Hooks cannot be called inside react-markdown's `components` map — those entries are plain
 * functions invoked mid-render, not components — so each reference is its own component here.
 */

import { isValidElement, ReactNode } from 'react';
import { defaultUrlTransform, type UrlTransform } from 'react-markdown';
import { ActionIcon, Box, Group, Image, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { Artifact } from '~api';
import { useArtifactLabel, useTrackArtifactDownload } from '~portal/contexts';
import { artifactDownloadName, downloadFile } from '~portal/lib';
import { useArtifactSignedUrl } from '~portal/stores/artifact-store';
import { typo } from '../typography.css';
import { getFormatIcon } from './artifact-format';
import { artifactRef } from './ArtifactRef.css';
import { useArtifactViewer } from './ArtifactViewer';
import { publishIntrinsicRatio } from './image-ratio';

/** `artifact:<id>` — the only non-http destination the answer contract admits. */
const ARTIFACT_URL_RE = /^artifact:([A-Za-z0-9_-]+)$/;

/**
 * @desc The artefact id in a markdown destination, or null if it is an ordinary URL.
 */
export const artifactIdFrom = (url: string | undefined): string | null =>
  (url === undefined ? null : ARTIFACT_URL_RE.exec(url)?.[1]) ?? null;

/**
 * @desc Keep `artifact:` destinations intact through react-markdown's URL sanitiser.
 *
 * `defaultUrlTransform` returns '' for every scheme outside http/https/mailto/irc(s)/xmpp, which
 * silently empties `src`/`href` — precisely how a chart became bare alt text. Everything else
 * still goes through the default, so this widens the allow-list by exactly one scheme rather
 * than disabling the sanitiser.
 */
export const artifactUrlTransform: UrlTransform = (url) =>
  ARTIFACT_URL_RE.test(url) ? url : defaultUrlTransform(url);

/**
 * A trailing `(CSV)` / `(xlsx)` in the model's own link text. The chip names the file and its
 * extension itself, so the parenthesised format is a duplicate — stripped here as a defence, the
 * primary fix being the wording of the python_executor contract that taught the habit. Anchored
 * to the end and limited to the known formats, so an ordinary parenthetical — `(2004-2031)`, a
 * currency, a footnote — is untouched.
 */
const FORMAT_SUFFIX_RE = new RegExp(
  `\\s*\\((?:${Object.values(Artifact.formats).join('|')})\\)\\s*$`,
  'i',
);

/** @desc The model's link text with a redundant trailing format in parentheses removed. */
export const stripFormatSuffix = (text: string): string =>
  text.replace(FORMAT_SUFFIX_RE, '').trim();

/**
 * @desc The plain text of a markdown link's children. Link text is nearly always a bare string,
 * but emphasis or code inside it arrives as elements, and the chip's caption is text either way.
 */
export const textOf = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return '';
};

/**
 * The shape of a markdown node this module reads. Structural rather than hast's own `Element`:
 * three fields decide the question, and a local type keeps the rule readable beside it.
 */
type MdNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: MdNode[];
};

/** @desc A node's children with whitespace-only text dropped — markdown leaves plenty of it. */
const meaningful = (node: MdNode | undefined): MdNode[] =>
  (node?.children ?? []).filter(
    (child) => !(child.type === 'text' && (child.value ?? '').trim() === ''),
  );

/** @desc The plain text under a markdown node. */
const nodeText = (node: MdNode): string =>
  node.type === 'text' ? (node.value ?? '') : (node.children ?? []).map(nodeText).join('');

/**
 * @desc The artefact link a paragraph or list item consists *entirely* of, or null.
 *
 * This is the whole of ADR-22's context rule: the same link is a chip inside a sentence and a
 * card when it stands alone, and the renderer already knows which it is looking at (ADR-23
 * decision 1). A list item is unwrapped one level first — a tight list gives the link directly,
 * a loose one wraps it in a paragraph, and the reader cannot tell the two apart.
 */
export const soleArtifactLink = (node: unknown): { artifactId: string; label: string } | null => {
  const children = meaningful(node as MdNode | undefined);
  if (children.length !== 1) return null;

  const only = children[0];
  if (only === undefined || only.type !== 'element') return null;
  if (only.tagName === 'p') return soleArtifactLink(only);
  if (only.tagName !== 'a') return null;

  const href = only.properties?.href;
  const artifactId = artifactIdFrom(typeof href === 'string' ? href : undefined);
  return artifactId === null ? null : { artifactId, label: nodeText(only) };
};

/**
 * @desc Whether a paragraph is nothing but an image.
 *
 * An image renders as a block — a Mantine `Image`, or the skeleton standing in for one — and a
 * `<div>` inside a `<p>` is invalid HTML that React reports as a hydration error and the browser
 * silently repairs by closing the paragraph early. So a paragraph the model wrote purely to
 * carry a figure is rendered as a `div` instead, keeping the paragraph's own classes and rhythm.
 */
export const isSoleImageParagraph = (node: unknown): boolean => {
  const children = meaningful(node as MdNode | undefined);
  const only = children[0];
  return children.length === 1 && only?.type === 'element' && only.tagName === 'img';
};

/** @desc Whether every item of a list is an artefact link standing alone — a list of cards. */
export const isArtifactCardList = (node: unknown): boolean => {
  const items = meaningful(node as MdNode | undefined);
  return items.length > 0 && items.every((item) => soleArtifactLink(item) !== null);
};

export function Unavailable({ label, title }: { label: ReactNode; title?: string }) {
  return (
    <Box component="span" className={artifactRef.unavailable}>
      <IconAlertTriangle size={14} />
      <Text component="span" size="xs" fw={500}>
        {title ?? 'Chart unavailable'}
      </Text>
      {label ? (
        <Text component="span" size="xs" c="dimmed">
          {label}
        </Text>
      ) : null}
    </Box>
  );
}

/**
 * @desc An image artefact embedded inline in the prose, clickable through to the turn's image
 * carousel at this image. A reference that will not resolve renders an explicit marker carrying
 * the alt text — never an `<img>` with no `src`, which is the defect this replaces.
 */
export function ArtifactImage({ artifactId, alt }: { artifactId: string; alt: string }) {
  const { url, format, isLoading } = useArtifactSignedUrl(artifactId);
  const { open, standalone } = useArtifactViewer();

  if (isLoading) return <Skeleton height={220} radius="md" my="sm" />;

  // The harness rejects an image embed of a non-image artefact, so this shape should never
  // arrive — but if it does, a download card is useful where a broken image is not.
  if (format !== null && !Artifact.isImageFormat(format)) {
    return <ArtifactLink artifactId={artifactId}>{alt}</ArtifactLink>;
  }

  if (!url) return <Unavailable label={alt} />;

  return (
    <>
      <UnstyledButton onClick={() => open(artifactId)} className={artifactRef.imageButton}>
        <Image src={url} alt={alt} className={typo.md.img} onLoad={publishIntrinsicRatio} />
      </UnstyledButton>
      {standalone}
    </>
  );
}

/**
 * @desc An artefact mentioned inside a sentence: a compact one-line chip carrying the format's
 * icon, the model's own title for it and the file format as a dimmed suffix. The file's real
 * name (`<name>.<format>`) is what the download saves as and what the tooltip shows, but it is
 * not the caption: the sentence's own wording is what the user is reading, so it leads, and the
 * file name stays a hover-away detail rather than crowding the one-line chip.
 *
 * Images are preview-first (ADR-22 decision 6): the chip's primary action opens the viewer, and
 * a secondary icon downloads. Everything else downloads on click, since there is nothing to
 * preview. The chip is capped at ~17rem — an artefact link that is a paragraph or bullet of its
 * own is a *card*, not a long chip, so the chip only ever has to carry a phrase.
 */
export function ArtifactLink({
  artifactId,
  children,
}: {
  artifactId: string;
  children: ReactNode;
}) {
  const { url, format, name, isLoading } = useArtifactSignedUrl(artifactId);
  const { open, standalone } = useArtifactViewer();
  const trackDownload = useTrackArtifactDownload();
  /**
   * The transcript's own answer first: `labelByArtifactId` is folded from the tool-output items
   * this conversation already holds, so it needs no request and is right even while the signed
   * URL is still resolving. The signed URL's `name` covers what it cannot — an artefact of
   * another conversation, which has no item here — and the id-based name is the floor under both
   * (ADR-21).
   */
  const lineageLabel = useArtifactLabel(artifactId);

  // A span, not the default div: this stands in for a chip *inside a sentence*, and a block
  // element there is invalid markup the browser repairs by breaking the paragraph in two.
  if (isLoading)
    return <Skeleton component="span" display="inline-block" height={24} width={180} radius="xl" />;
  if (!url) return <Unavailable label={children} title="File unavailable" />;

  const extension = format ?? 'file';
  const fileName = lineageLabel ?? artifactDownloadName(artifactId, extension, name ?? undefined);
  // The model's own wording, minus the format it should not have been repeating — the chip's
  // title. Falls back to the file name when the model gave nothing usable, i.e. no label or one
  // that only repeats the file name.
  const label = stripFormatSuffix(textOf(children));
  const title =
    label.length > 0 && label.toLowerCase() !== fileName.toLowerCase() ? label : fileName;
  const isImage = format !== null && Artifact.isImageFormat(format);

  const download = () => {
    trackDownload(extension, 'chip');
    downloadFile(url, fileName);
  };

  return (
    // A span rather than a button: an image chip carries a second control (download), and a
    // button inside a button is not a thing the platform allows.
    <Box component="span" className={artifactRef.card}>
      <UnstyledButton
        className={artifactRef.trigger}
        title={fileName}
        onClick={() => (isImage ? open(artifactId) : download())}
      >
        <Group gap={6} wrap="nowrap" className={artifactRef.content}>
          <Box component="span" className={artifactRef.icon}>
            {getFormatIcon(extension, 16)}
          </Box>
          <Text component="span" className={artifactRef.title}>
            {title}
          </Text>
          <Text component="span" className={artifactRef.format}>
            {extension.toUpperCase()}
          </Text>
        </Group>
      </UnstyledButton>
      {isImage && (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          className={artifactRef.secondary}
          aria-label={`Download ${fileName}`}
          data-dd-action-name="Download file"
          onClick={download}
        >
          <IconDownload size={14} />
        </ActionIcon>
      )}
      {standalone}
    </Box>
  );
}
